import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../context/AppContext";
import AdminNavbar from "../components/AdminNavbar";
import toast from "react-hot-toast";

const LOW_THRESHOLD = 10;

const SECTION_COLORS = {
  juices: "#2D8B4E",
  salads: "#16a34a",
  sandwiches: "#d97706",
  smoothies: "#7c3aed",
};

// sections are loaded from backend — this is just a fallback label helper
const catLabel = (c, sections = []) => {
  for (const sec of sections) {
    const found = sec.categories?.find(
      (cat) => cat.key === c || cat.value === c,
    );
    if (found) return found.label.replace(/^[^\s]+\s/, "");
  }
  return c
    .split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
};

const StockBadge = ({ stock }) => {
  if (stock === 0)
    return (
      <span className="chip bg-red-100 text-red-700 border border-red-200 text-xs font-bold">
        ⊗ Out of Stock
      </span>
    );
  if (stock < LOW_THRESHOLD)
    return (
      <span className="chip bg-amber-100 text-amber-700 border border-amber-200 text-xs font-bold">
        ⚠ Low ({stock})
      </span>
    );
  return (
    <span className="chip bg-green-100 text-green-700 border border-green-200 text-xs font-bold">
      ✓ {stock} in stock
    </span>
  );
};

/* ── small reusable label ── */
const LX = ({ children }) => (
  <label
    style={{
      display: "block",
      fontSize: 11,
      fontWeight: 700,
      color: "#6b7280",
      marginBottom: 4,
      textTransform: "uppercase",
      letterSpacing: "0.4px",
    }}
  >
    {children}
  </label>
);

/* ── section header inside modal ── */
const Sec = ({ icon, title, color = "#2D8B4E" }) => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}
  >
    <div
      style={{
        width: 34,
        height: 34,
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 16,
        flexShrink: 0,
        background: `${color}18`,
        color,
      }}
    >
      {icon}
    </div>
    <h3 style={{ fontWeight: 800, fontSize: 13, color, margin: 0 }}>{title}</h3>
  </div>
);

/* ── input style ── */
const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: 12,
  border: "1.5px solid rgba(45,139,78,0.2)",
  background: "rgba(255,255,255,0.7)",
  fontSize: 13,
  color: "#111827",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s, box-shadow 0.15s",
  fontFamily: "inherit",
};
const InputField = ({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  required = false,
  ...rest
}) => (
  <div>
    <LX>
      {label}
      {required && " *"}
    </LX>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={inputStyle}
      onFocus={(e) => {
        e.target.style.borderColor = "#2D8B4E";
        e.target.style.boxShadow = "0 0 0 3px rgba(45,139,78,0.1)";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "rgba(45,139,78,0.2)";
        e.target.style.boxShadow = "none";
      }}
      {...rest}
    />
  </div>
);

/* ══════════════════════════════════════════════════
   EDIT PRODUCT MODAL
══════════════════════════════════════════════════ */
function EditProductModal({ product, onClose, onSaved, sections = [] }) {
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: product.name || "",
    description: product.description || "",
    price: product.price ?? "",
    originalPrice: product.originalPrice ?? "",
    category: product.category || "",
    volume: product.volume || "",
    prepTime: product.prepTime || "",
    stock: product.stock ?? 0,
    isBestseller: product.isBestseller || false,
    isFeatured: product.isFeatured || false,
    isAvailable: product.isAvailable !== false,
    unavailableMessage: product.unavailableMessage || "",
    ingredients: Array.isArray(product.ingredients)
      ? product.ingredients.join(", ")
      : product.ingredients || "",
    tags: Array.isArray(product.tags)
      ? product.tags.join(", ")
      : product.tags || "",
    calories: product.nutritionInfo?.calories ?? "",
    protein: product.nutritionInfo?.protein ?? "",
    carbs: product.nutritionInfo?.carbs ?? "",
    fiber: product.nutritionInfo?.fiber ?? "",
  });

  /* image state */
  const [imgTab, setImgTab] = useState("url");
  const [urlInput, setUrlInput] = useState(product.image || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [cloudUrl, setCloudUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic"); // basic | pricing | image | flags

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));
  const setChk = (f) => (e) =>
    setForm((p) => ({ ...p, [f]: e.target.checked }));

  /* image pick/drop */
  const acceptFile = useCallback((f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Max file size is 5 MB");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setCloudUrl("");
  }, []);
  const onFilePick = (e) => acceptFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);
  const clearImage = () => {
    setFile(null);
    setPreview("");
    setCloudUrl("");
    setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
  };

  /* cloudinary upload */ const uploadNow = useCallback(async () => {
    if (!file) return null;
    setUploading(true);
    setProgress(0);
    try {
      // 1. Get signed credentials from backend
      let signData;
      try {
        const { data } = await api.post("/products/cloudinary-sign");
        signData = data;
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          "Could not reach server for upload credentials";
        toast.error(msg);
        return null;
      }

      const { cloudName, apiKey, signature, timestamp, folder } = signData;

      // 2. Build FormData — ONLY include fields that were part of the signature
      //    Backend signed:  folder=SARVA-products&timestamp=<ts>
      //    Any extra params here MUST also have been included in the signature string
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", apiKey);
      fd.append("signature", signature);
      fd.append("timestamp", String(timestamp));
      fd.append("folder", folder); // matches what backend signed

      // 3. Upload directly to Cloudinary
      const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;

      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable)
            setProgress(Math.round((e.loaded / e.total) * 100));
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              reject(new Error("Invalid response from Cloudinary"));
            }
          } else {
            try {
              const errJson = JSON.parse(xhr.responseText);
              reject(
                new Error(
                  errJson?.error?.message ||
                    `Upload failed (HTTP ${xhr.status})`,
                ),
              );
            } catch {
              reject(new Error(`Upload failed — HTTP ${xhr.status}`));
            }
          }
        };

        xhr.onerror = () =>
          reject(new Error("Network error — check your internet connection"));
        xhr.ontimeout = () => reject(new Error("Upload timed out"));
        xhr.timeout = 60000; // 60s

        xhr.open("POST", uploadUrl);
        xhr.send(fd);
      });

      setCloudUrl(result.secure_url);
      setProgress(100);
      toast.success("Image uploaded!");
      return result.secure_url;
    } catch (err) {
      toast.error("Upload failed: " + err.message);
      setProgress(0);
      return null;
    } finally {
      setUploading(false);
    }
  }, [file]);

  /* resolve final image URL */
  const getFinalImage = async () => {
    if (imgTab === "url") return urlInput.trim();
    if (cloudUrl) return cloudUrl;
    if (file) return await uploadNow();
    return product.image; // unchanged
  };

  /* submit */
  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Product name is required");
      return;
    }
    if (!form.category) {
      toast.error("Category is required");
      return;
    }
    if (!form.price || +form.price <= 0) {
      toast.error("Valid price required");
      return;
    }
    setSaving(true);
    try {
      const finalImg = await getFinalImage();
      if (!finalImg) {
        toast.error("Product image is required");
        setSaving(false);
        return;
      }

      await api.put(`/products/${product._id}`, {
        name: form.name.trim(),
        description: form.description.trim(),
        price: +form.price,
        originalPrice: form.originalPrice ? +form.originalPrice : null,
        category: form.category,
        image: finalImg,
        volume: form.volume,
        prepTime: form.prepTime,
        stock: +form.stock,
        isBestseller: form.isBestseller,
        isFeatured: form.isFeatured,
        isAvailable: form.isAvailable,
        unavailableMessage: form.unavailableMessage?.trim() || "",
        ingredients: form.ingredients
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        tags: form.tags
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        nutritionInfo: {
          calories: form.calories ? +form.calories : undefined,
          protein: form.protein || undefined,
          carbs: form.carbs || undefined,
          fiber: form.fiber || undefined,
        },
      });
      toast.success(`${form.name} updated!`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { key: "basic", label: "Basic", icon: "📝" },
    { key: "pricing", label: "Pricing", icon: "💰" },
    { key: "image", label: "Image", icon: "🖼️" },
    { key: "flags", label: "Flags", icon: "🚩" },
  ];

  const displayImg =
    imgTab === "url" ? urlInput : preview || cloudUrl || product.image;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      {/* backdrop */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(4px)",
        }}
        onClick={onClose}
      />

      {/* modal */}
      <div
        style={{
          position: "relative",
          background: "#fff",
          borderRadius: 24,
          width: "100%",
          maxWidth: 680,
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
          overflow: "hidden",
          animation: "modalIn 0.22s cubic-bezier(0.34,1.3,0.64,1)",
        }}
      >
        {/* ── modal header ── */}
        <div
          style={{
            padding: "18px 24px 14px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexShrink: 0,
          }}
        >
          <img
            src={displayImg || product.image}
            alt={product.name}
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              objectFit: "cover",
              border: "1.5px solid rgba(45,139,78,0.2)",
              flexShrink: 0,
            }}
            onError={(e) => (e.target.style.display = "none")}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                fontSize: 17,
                fontWeight: 900,
                color: "#0f172a",
                margin: 0,
                letterSpacing: "-0.3px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Edit: {product.name}
            </h2>
            <p
              style={{
                fontSize: 12,
                color: "#9ca3af",
                margin: 0,
                marginTop: 2,
              }}
            >
              {catLabel(product.category, sections)} · ₹{product.price}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              border: "1.5px solid #e5e7eb",
              background: "#f9fafb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#6b7280",
              fontSize: 16,
              flexShrink: 0,
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#fee2e2";
              e.currentTarget.style.borderColor = "#fca5a5";
              e.currentTarget.style.color = "#ef4444";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#f9fafb";
              e.currentTarget.style.borderColor = "#e5e7eb";
              e.currentTarget.style.color = "#6b7280";
            }}
          >
            ✕
          </button>
        </div>

        {/* ── inner tab bar ── */}
        <div
          style={{
            display: "flex",
            gap: 2,
            padding: "10px 24px 0",
            borderBottom: "1px solid #f0f0f0",
            flexShrink: 0,
            overflowX: "auto",
          }}
        >
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "8px 14px 10px",
                border: "none",
                background: "none",
                cursor: "pointer",
                fontSize: 12.5,
                fontWeight: activeTab === t.key ? 800 : 600,
                color: activeTab === t.key ? "#2D8B4E" : "#6b7280",
                borderBottom:
                  activeTab === t.key
                    ? "2.5px solid #2D8B4E"
                    : "2.5px solid transparent",
                transition: "all 0.15s",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ fontSize: 14 }}>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── tab content (scrollable) ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {/* ─ BASIC ─ */}
          {activeTab === "basic" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Sec icon="📝" title="Basic Information" />
              <InputField
                label="Product Name"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. Mango Burst"
              />
              <div>
                <LX>Description</LX>
                <textarea
                  value={form.description}
                  onChange={set("description")}
                  placeholder="Describe the product…"
                  style={{ ...inputStyle, height: 84, resize: "none" }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#2D8B4E";
                    e.target.style.boxShadow = "0 0 0 3px rgba(45,139,78,0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgba(45,139,78,0.2)";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}
              >
                <div>
                  <LX>Category *</LX>
                  <select
                    value={form.category}
                    onChange={set("category")}
                    style={{ ...inputStyle }}
                  >
                    <option value="">Select Category</option>
                    {sections.map((s) => (
                      <optgroup key={s.key} label={`${s.emoji} ${s.label}`}>
                        {(s.categories || []).map((c) => (
                          <option key={c.key} value={c.key}>
                            {c.emoji} {c.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                {/* Volume with smart unit based on section */}
                {(() => {
                  const cat = form.category;
                  const weightCats = [
                    "fruit-salad",
                    "bowls",
                    "veg-sandwiches",
                    "grilled",
                    "wraps",
                  ];
                  const isWeight =
                    weightCats.includes(cat) ||
                    ["salads", "sandwiches"].includes(
                      (() => {
                        const sec = [
                          { key: "salads", cats: ["fruit-salad", "bowls"] },
                          {
                            key: "sandwiches",
                            cats: ["veg-sandwiches", "grilled", "wraps"],
                          },
                        ];
                        return sec.find((s) => s.cats.includes(cat))?.key || "";
                      })(),
                    );
                  const unit = isWeight ? "gms" : "ml";
                  const ph = isWeight ? `e.g. 250${unit}` : `e.g. 300${unit}`;
                  const uCol = isWeight ? "#d97706" : "#2D8B4E";
                  return (
                    <div>
                      <LX>
                        Volume / Size{" "}
                        <span
                          style={{
                            marginLeft: 6,
                            padding: "1px 6px",
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 800,
                            background: `${uCol}18`,
                            color: uCol,
                            letterSpacing: "0.3px",
                          }}
                        >
                          {unit}
                        </span>
                      </LX>
                      <div style={{ position: "relative" }}>
                        <input
                          value={form.volume}
                          onChange={set("volume")}
                          placeholder={ph}
                          style={{ ...inputStyle, paddingRight: 42 }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#2D8B4E";
                            e.target.style.boxShadow =
                              "0 0 0 3px rgba(45,139,78,0.1)";
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = "rgba(45,139,78,0.2)";
                            e.target.style.boxShadow = "none";
                          }}
                        />
                        <span
                          style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: 11,
                            fontWeight: 800,
                            color: uCol,
                            pointerEvents: "none",
                          }}
                        >
                          {unit}
                        </span>
                      </div>
                    </div>
                  );
                })()}
                <InputField
                  label="Prep Time"
                  value={form.prepTime}
                  onChange={set("prepTime")}
                  placeholder="5 min"
                />
              </div>
              <InputField
                label="Ingredients (comma-separated)"
                value={form.ingredients}
                onChange={set("ingredients")}
                placeholder="Mango, Himalayan Salt, Lemon"
              />
              <InputField
                label="Tags (comma-separated)"
                value={form.tags}
                onChange={set("tags")}
                placeholder="mango, tropical, vitamin c"
              />
              <Sec icon="📊" title="Nutrition Info" color="#2196F3" />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4,1fr)",
                  gap: 10,
                }}
              >
                {[
                  { l: "Calories", f: "calories", t: "number" },
                  { l: "Protein", f: "protein", placeholder: "1.5g" },
                  { l: "Carbs", f: "carbs", placeholder: "28g" },
                  { l: "Fiber", f: "fiber", placeholder: "2g" },
                ].map(({ l, f, t, placeholder }) => (
                  <InputField
                    key={f}
                    label={l}
                    type={t || "text"}
                    value={form[f]}
                    onChange={set(f)}
                    placeholder={placeholder}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ─ PRICING ─ */}
          {activeTab === "pricing" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Sec icon="💰" title="Pricing & Stock" color="#FF6B35" />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 12,
                }}
              >
                <InputField
                  label="Price (₹)"
                  required
                  type="number"
                  value={form.price}
                  onChange={set("price")}
                  placeholder="99"
                />
                <InputField
                  label="Original Price"
                  type="number"
                  value={form.originalPrice}
                  onChange={set("originalPrice")}
                  placeholder="Optional"
                />
                <InputField
                  label="Stock"
                  type="number"
                  value={form.stock}
                  onChange={set("stock")}
                />
              </div>
              {/* live preview */}
              <div
                style={{
                  background: "rgba(45,139,78,0.05)",
                  border: "1px solid rgba(45,139,78,0.14)",
                  borderRadius: 14,
                  padding: "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <p
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      margin: 0,
                    }}
                  >
                    Selling Price
                  </p>
                  <p
                    style={{
                      fontSize: 22,
                      fontWeight: 900,
                      color: "#15803d",
                      margin: "4px 0 0",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    ₹{form.price || "—"}
                  </p>
                </div>
                {form.originalPrice && (
                  <>
                    <div style={{ color: "#d1d5db", fontSize: 18 }}>→</div>
                    <div style={{ textAlign: "center" }}>
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: "0.8px",
                          margin: 0,
                        }}
                      >
                        Original
                      </p>
                      <p
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: "#9ca3af",
                          textDecoration: "line-through",
                          margin: "4px 0 0",
                        }}
                      >
                        ₹{form.originalPrice}
                      </p>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <p
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#9ca3af",
                          textTransform: "uppercase",
                          letterSpacing: "0.8px",
                          margin: 0,
                        }}
                      >
                        Discount
                      </p>
                      <p
                        style={{
                          fontSize: 16,
                          fontWeight: 900,
                          color: "#16a34a",
                          margin: "4px 0 0",
                        }}
                      >
                        {Math.round(
                          ((+form.originalPrice - +form.price) /
                            +form.originalPrice) *
                            100,
                        )}
                        % OFF
                      </p>
                    </div>
                  </>
                )}
                <div style={{ textAlign: "center", marginLeft: "auto" }}>
                  <p
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      margin: 0,
                    }}
                  >
                    Stock
                  </p>
                  <p
                    style={{
                      fontSize: 22,
                      fontWeight: 900,
                      color:
                        +form.stock === 0
                          ? "#ef4444"
                          : +form.stock < 10
                            ? "#f59e0b"
                            : "#374151",
                      margin: "4px 0 0",
                    }}
                  >
                    {form.stock}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ─ IMAGE ─ */}
          {activeTab === "image" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Sec icon="🖼️" title="Product Image" />

              {/* current image */}
              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: 14,
                  padding: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  border: "1px solid #e5e7eb",
                }}
              >
                <img
                  src={product.image}
                  alt="current"
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 10,
                    objectFit: "cover",
                    border: "1.5px solid rgba(45,139,78,0.2)",
                    flexShrink: 0,
                  }}
                  onError={(e) => (e.target.style.display = "none")}
                />
                <div style={{ minWidth: 0 }}>
                  <p
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      margin: "0 0 2px",
                    }}
                  >
                    Current Image
                  </p>
                  <p
                    style={{
                      fontSize: 11,
                      color: "#94a3b8",
                      margin: 0,
                      wordBreak: "break-all",
                    }}
                  >
                    {product.image?.slice(0, 60)}
                    {product.image?.length > 60 ? "…" : ""}
                  </p>
                </div>
              </div>

              {/* tab switcher */}
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  padding: 4,
                  borderRadius: 12,
                  background: "rgba(45,139,78,0.07)",
                  border: "1.5px solid rgba(45,139,78,0.12)",
                }}
              >
                {[
                  { k: "url", l: "🔗 URL" },
                  { k: "upload", l: "📁 Upload" },
                ].map((m) => (
                  <button
                    key={m.k}
                    onClick={() => setImgTab(m.k)}
                    style={{
                      flex: 1,
                      padding: "7px",
                      borderRadius: 8,
                      border: "none",
                      cursor: "pointer",
                      background:
                        imgTab === m.k
                          ? "linear-gradient(135deg,#2D8B4E,#4CAF72)"
                          : "transparent",
                      color: imgTab === m.k ? "#fff" : "#9ca3af",
                      fontSize: 12,
                      fontWeight: 700,
                      transition: "all 0.15s",
                    }}
                  >
                    {m.l}
                  </button>
                ))}
              </div>

              {/* URL tab */}
              {imgTab === "url" && (
                <div>
                  <LX>Image URL</LX>
                  <input
                    type="text"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://images.unsplash.com/…"
                    style={inputStyle}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#2D8B4E";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(45,139,78,0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "rgba(45,139,78,0.2)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  {urlInput && (
                    <div
                      style={{
                        marginTop: 10,
                        borderRadius: 14,
                        overflow: "hidden",
                        height: 140,
                      }}
                    >
                      <img
                        src={urlInput}
                        alt="preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Upload tab */}
              {imgTab === "upload" && (
                <div>
                  {!preview && !cloudUrl ? (
                    <div
                      onClick={() => fileRef.current?.click()}
                      onDrop={onDrop}
                      onDragOver={onDragOver}
                      onDragLeave={onDragLeave}
                      style={{
                        border: `2px dashed ${dragOver ? "#2D8B4E" : "#bbf7d0"}`,
                        borderRadius: 16,
                        padding: "32px 20px",
                        textAlign: "center",
                        cursor: "pointer",
                        background: dragOver
                          ? "rgba(45,139,78,0.04)"
                          : "transparent",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{ fontSize: 36, marginBottom: 8 }}>📸</div>
                      <p
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#374151",
                          margin: 0,
                        }}
                      >
                        Click or drag & drop
                      </p>
                      <p
                        style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}
                      >
                        PNG · JPG · WEBP — max 5 MB
                      </p>
                    </div>
                  ) : (
                    <div style={{ position: "relative" }}>
                      <img
                        src={preview || cloudUrl}
                        alt="preview"
                        style={{
                          width: "100%",
                          height: 140,
                          objectFit: "cover",
                          borderRadius: 14,
                          display: "block",
                        }}
                      />
                      <button
                        onClick={clearImage}
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: "rgba(0,0,0,0.55)",
                          border: "none",
                          color: "#fff",
                          fontSize: 14,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background =
                            "rgba(239,68,68,0.85)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background =
                            "rgba(0,0,0,0.55)")
                        }
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={onFilePick}
                  />

                  {file && !cloudUrl && (
                    <div style={{ marginTop: 10 }}>
                      <p
                        style={{
                          fontSize: 11,
                          color: "#6b7280",
                          fontWeight: 600,
                          marginBottom: 6,
                        }}
                      >
                        📄 {file.name}
                      </p>
                      {uploading ? (
                        <div>
                          <div
                            style={{
                              height: 4,
                              background: "#f0f0f0",
                              borderRadius: 2,
                              overflow: "hidden",
                              marginBottom: 4,
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: `${progress}%`,
                                background:
                                  "linear-gradient(90deg,#2D8B4E,#4CAF72)",
                                borderRadius: 2,
                                transition: "width 0.2s",
                              }}
                            />
                          </div>
                          <p
                            style={{
                              fontSize: 11,
                              color: "#2D8B4E",
                              fontWeight: 700,
                            }}
                          >
                            {progress}% uploading…
                          </p>
                        </div>
                      ) : (
                        <button
                          onClick={uploadNow}
                          style={{
                            width: "100%",
                            padding: "10px",
                            borderRadius: 11,
                            border: "none",
                            cursor: "pointer",
                            background:
                              "linear-gradient(135deg,#2D8B4E,#4CAF72)",
                            color: "#fff",
                            fontSize: 13,
                            fontWeight: 700,
                            boxShadow: "0 3px 12px rgba(45,139,78,0.3)",
                          }}
                        >
                          ☁️ Upload to Cloudinary
                        </button>
                      )}
                    </div>
                  )}
                  {cloudUrl && (
                    <div
                      style={{
                        marginTop: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        borderRadius: 12,
                        padding: "10px 12px",
                      }}
                    >
                      <span style={{ color: "#16a34a" }}>✅</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "#15803d",
                            margin: 0,
                          }}
                        >
                          Saved to Cloudinary
                        </p>
                        <p
                          style={{
                            fontSize: 10,
                            color: "#9ca3af",
                            margin: "2px 0 0",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {cloudUrl}
                        </p>
                      </div>
                      <button
                        onClick={clearImage}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#9ca3af",
                          fontSize: 14,
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ─ FLAGS ─ */}
          {activeTab === "flags" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Sec icon="🚩" title="Product Flags" color="#FF6B35" />
              {[
                {
                  label: "Bestseller",
                  field: "isBestseller",
                  desc: "Show bestseller badge on product",
                  icon: "⭐",
                },
                {
                  label: "Featured",
                  field: "isFeatured",
                  desc: "Show product on homepage hero section",
                  icon: "🌟",
                },
                {
                  label: "Available",
                  field: "isAvailable",
                  desc: "Product is visible to customers",
                  icon: "👁",
                },
              ].map(({ label, field, desc, icon }) => (
                <label
                  key={field}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "14px 16px",
                    borderRadius: 14,
                    cursor: "pointer",
                    background: form[field]
                      ? "rgba(45,139,78,0.05)"
                      : "#fafafa",
                    border: `1.5px solid ${form[field] ? "rgba(45,139,78,0.2)" : "#e5e7eb"}`,
                    transition: "all 0.15s",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <span style={{ fontSize: 20 }}>{icon}</span>
                    <div>
                      <p
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#111827",
                          margin: 0,
                        }}
                      >
                        {label}
                      </p>
                      <p
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          margin: "2px 0 0",
                        }}
                      >
                        {desc}
                      </p>
                    </div>
                  </div>
                  {/* toggle */}
                  <div
                    style={{
                      position: "relative",
                      width: 44,
                      height: 24,
                      flexShrink: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form[field]}
                      onChange={setChk(field)}
                      style={{
                        position: "absolute",
                        opacity: 0,
                        width: 0,
                        height: 0,
                      }}
                    />
                    <div
                      style={{
                        width: 44,
                        height: 24,
                        borderRadius: 12,
                        background: form[field] ? "#22c55e" : "#e5e7eb",
                        transition: "background 0.2s",
                        boxShadow: form[field]
                          ? "0 0 0 2px rgba(34,197,94,0.2)"
                          : "none",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 2,
                        left: form[field] ? 22 : 2,
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: "#fff",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                        transition: "left 0.2s",
                      }}
                    />
                  </div>
                </label>
              ))}
              {/* Unavailable message — shown only when product is hidden */}
              {!form.isAvailable && (
                <div style={{ marginTop: 4 }}>
                  <LX>
                    Unavailable Message{" "}
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 500,
                        color: "#9ca3af",
                        textTransform: "none",
                        letterSpacing: 0,
                      }}
                    >
                      (shown to customers)
                    </span>
                  </LX>
                  <textarea
                    value={form.unavailableMessage}
                    onChange={set("unavailableMessage")}
                    placeholder="e.g. Back in stock tomorrow! / Seasonal item — available next week"
                    rows={2}
                    style={{ ...inputStyle, resize: "none", height: 60 }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#FF6B35";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(255,107,53,0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "rgba(45,139,78,0.2)";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  <p style={{ fontSize: 10, color: "#9ca3af", marginTop: 3 }}>
                    This message will appear on the product card and detail page
                    for customers.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── footer buttons ── */}
        <div
          style={{
            padding: "14px 24px",
            borderTop: "1px solid #f0f0f0",
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            flexShrink: 0,
            background: "#fff",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              borderRadius: 11,
              border: "1.5px solid #e5e7eb",
              background: "#fff",
              color: "#374151",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            style={{
              padding: "10px 24px",
              borderRadius: 11,
              border: "none",
              background: saving
                ? "#9ca3af"
                : "linear-gradient(135deg,#2D8B4E,#4CAF72)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 800,
              cursor: saving ? "not-allowed" : "pointer",
              boxShadow: saving ? "none" : "0 4px 16px rgba(45,139,78,0.32)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 22px rgba(45,139,78,0.42)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = saving
                ? "none"
                : "0 4px 16px rgba(45,139,78,0.32)";
            }}
          >
            {saving ? (
              <>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    border: "2px solid rgba(255,255,255,0.4)",
                    borderTopColor: "#fff",
                    borderRadius: "50%",
                    animation: "spin 0.65s linear infinite",
                    display: "inline-block",
                  }}
                />{" "}
                Saving…
              </>
            ) : (
              "💾 Save Changes"
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalIn { from{opacity:0;transform:scale(0.96) translateY(12px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN PAGE — unchanged design
══════════════════════════════════════════════════ */
export default function AdminInventory() {
  const navigate = useNavigate();

  /* sections from backend */
  const [sections, setSections] = useState([]);

  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    outOfStock: 0,
    lowStock: 0,
    inStock: 0,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [section, setSection] = useState("all");
  const [category, setCategory] = useState("all");
  const [lowOnly, setLowOnly] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editStock, setEditStock] = useState("");
  const [editProduct, setEditProduct] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== "all") {
        params.append("category", category);
      } else if (section !== "all") {
        // use section param — backend maps it to the right categories
        params.append("section", section);
      }
      if (lowOnly) params.append("lowStock", "true");
      const res = await api.get(`/admin/inventory?${params}`);
      setProducts(res.data.products);
      setSummary(res.data.summary);
    } catch {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  }, [category, section, lowOnly]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  /* fetch sections from backend once */
  useEffect(() => {
    api
      .get("/products/sections/all")
      .then((res) => setSections(res.data?.sections || []))
      .catch(() => {});
  }, []);

  const displayed = search
    ? products.filter(
        (p) =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.category.toLowerCase().includes(search.toLowerCase()),
      )
    : products;

  const saveStock = async (id) => {
    try {
      await api.patch(`/admin/inventory/${id}`, { stock: +editStock });
      toast.success("Stock updated");
      setEditId(null);
      fetchInventory();
    } catch {
      toast.error("Failed to update stock");
    }
  };

  const toggleAvailable = async (id, current) => {
    try {
      await api.patch(`/admin/inventory/${id}`, { isAvailable: !current });
      toast.success(`Product ${current ? "hidden" : "visible"}`);
      fetchInventory();
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteProduct = async () => {
    if (!deleteItem) return;
    try {
      await api.delete(`/products/${deleteItem._id}`);
      toast.success(`${deleteItem.name} deleted`);
      setDeleteItem(null);
      fetchInventory();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg,#f0f9f0,#e8f5e9)" }}
    >
      <AdminNavbar />
      <div className="max-w-screen-xl mx-auto p-4 md:p-8">
        {/* header — unchanged */}
        <div className="flex justify-between items-start mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-black text-green-700">Inventory</h1>
            <p className="text-gray-400">
              Track and manage product stock levels
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => navigate("/admin/categories")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border-2 border-green-500/30 text-green-600 hover:bg-green-50 bg-white transition-all"
            >
              🗂 Manage Categories
            </button>
            <button
              onClick={() => navigate("/admin/products/add")}
              className="btn-primary flex items-center gap-2"
            >
              ➕ Add Product
            </button>
          </div>
        </div>

        {/* stats — unchanged */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            {
              icon: "📦",
              label: "Total Products",
              value: summary.total,
              color: "#2196F3",
            },
            {
              icon: "✅",
              label: "In Stock",
              value: summary.inStock,
              color: "#2D8B4E",
            },
            {
              icon: "⚠️",
              label: "Low Stock",
              value: summary.lowStock,
              color: "#FF6B35",
            },
            {
              icon: "⊗",
              label: "Out of Stock",
              value: summary.outOfStock,
              color: "#f44336",
            },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-2xl p-5 text-center backdrop-blur-sm"
              style={{
                background: `${s.color}12`,
                border: `1px solid ${s.color}25`,
              }}
            >
              <div className="text-2xl mb-1">{s.icon}</div>
              <div className="text-3xl font-black" style={{ color: s.color }}>
                {s.value}
              </div>
              <div className="text-xs text-gray-500 font-semibold mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* low stock warning — unchanged */}
        {summary.lowStock > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex items-center justify-between flex-wrap gap-2">
            <span className="text-amber-700 text-sm font-semibold">
              ⚠️ <strong>{summary.lowStock} products</strong> have low stock
              (below {LOW_THRESHOLD} units)
            </span>
            <button
              onClick={() => setLowOnly(true)}
              className="text-amber-700 text-sm font-bold underline"
            >
              Filter low stock
            </button>
          </div>
        )}

        {/* filters — section tabs + category + search */}
        <div className="glass-card p-4 mb-5">
          {/* ── Section tabs (dynamic from backend) ── */}
          <div
            className="flex gap-2 mb-3 overflow-x-auto pb-1"
            style={{ scrollbarWidth: "none" }}
          >
            <button
              onClick={() => {
                setSection("all");
                setCategory("all");
                setLowOnly(false);
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border-2 transition-all whitespace-nowrap flex-shrink-0"
              style={{
                background:
                  section === "all"
                    ? "linear-gradient(135deg,#2D8B4E,#4CAF72)"
                    : "white",
                color: section === "all" ? "white" : "#6b7280",
                borderColor: section === "all" ? "transparent" : "#e5e7eb",
                boxShadow:
                  section === "all"
                    ? "0 4px 14px rgba(45,139,78,0.28)"
                    : "none",
              }}
            >
              🗂 All Sections
            </button>
            {sections.map((s) => {
              const color = SECTION_COLORS[s.key] || s.color || "#6b7280";
              return (
                <button
                  key={s.key}
                  onClick={() => {
                    setSection(s.key);
                    setCategory("all");
                    setLowOnly(false);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border-2 transition-all whitespace-nowrap flex-shrink-0"
                  style={{
                    background: section === s.key ? color : "white",
                    color: section === s.key ? "white" : color,
                    borderColor:
                      section === s.key ? "transparent" : `${color}40`,
                    boxShadow:
                      section === s.key ? `0 4px 14px ${color}40` : "none",
                  }}
                >
                  {s.emoji} {s.label}
                </button>
              );
            })}
          </div>

          {/* ── Search + category + low stock ── */}
          <div className="grid sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500">
                🔍
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products…"
                className="input-glass pl-9 text-sm"
              />
            </div>
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setLowOnly(false);
              }}
              className="input-glass text-sm"
            >
              <option value="all">
                {section === "all"
                  ? "All Categories"
                  : `All ${sections.find((s) => s.key === section)?.label || ""}`}
              </option>
              {section === "all"
                ? sections.map((s) => (
                    <optgroup key={s.key} label={`${s.emoji} ${s.label}`}>
                      {(s.categories || []).map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.emoji} {c.label}
                        </option>
                      ))}
                    </optgroup>
                  ))
                : (
                    sections.find((s) => s.key === section)?.categories || []
                  ).map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.emoji} {c.label}
                    </option>
                  ))}
            </select>
            <div className="flex gap-2">
              <button
                onClick={() => setLowOnly(!lowOnly)}
                className={`flex-1 rounded-xl text-xs font-bold border-2 transition-all py-2 ${lowOnly ? "bg-amber-500 border-amber-500 text-white" : "border-amber-400 text-amber-600 hover:bg-amber-50"}`}
              >
                ⚠ Low Stock
              </button>
              <button
                onClick={fetchInventory}
                className="px-3 border-2 border-green-500/40 text-green-500 rounded-xl text-xs hover:bg-green-50 transition-colors"
              >
                ↻
              </button>
            </div>
          </div>
        </div>

        {/* table — unchanged structure, added Edit button */}
        <div className="admin-table-wrap overflow-hidden relative">
          {loading && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-green-500/20 overflow-hidden z-10">
              <div className="h-full bg-gradient-to-r from-green-400 to-green-600 animate-pulse" />
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead className="admin-thead">
                <tr>
                  {[
                    "Product",
                    "Category",
                    "Price",
                    "Stock",
                    "Availability",
                    "Actions",
                  ].map((h) => (
                    <th key={h} className="admin-th">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-gray-400">
                      No products found
                    </td>
                  </tr>
                ) : (
                  displayed.map((p, idx) => (
                    <tr
                      key={p._id}
                      className="admin-tr"
                      style={{ animationDelay: `${idx * 0.04}s` }}
                    >
                      {/* product — unchanged */}
                      <td className="admin-td">
                        <div className="flex items-center gap-3">
                          <img
                            src={p.image}
                            alt={p.name}
                            className="w-10 h-10 rounded-xl object-cover"
                            style={{
                              border: "1.5px solid rgba(45,139,78,0.2)",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            }}
                          />
                          <div>
                            <p className="font-bold leading-tight">{p.name}</p>
                            <p className="text-xs text-gray-400">{p.volume}</p>
                          </div>
                        </div>
                      </td>

                      {/* category — unchanged */}
                      <td className="admin-td">
                        <span className="status-chip bg-blue-50 text-blue-700 border border-blue-100">
                          {catLabel(p.category, sections)}
                        </span>
                      </td>

                      {/* price — unchanged */}
                      <td className="admin-td">
                        <span className="font-black text-green-600 text-base">
                          ₹{p.price}
                        </span>
                      </td>

                      {/* stock inline edit — unchanged */}
                      <td className="px-4 py-3">
                        {editId === p._id ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              value={editStock}
                              onChange={(e) => setEditStock(e.target.value)}
                              onKeyDown={(e) =>
                                e.key === "Enter" && saveStock(p._id)
                              }
                              className="w-20 px-2 py-1.5 text-sm border border-green-300 rounded-lg focus:outline-none focus:border-green-500"
                              autoFocus
                            />
                            <button
                              onClick={() => saveStock(p._id)}
                              className="text-green-500 hover:text-green-700 font-bold text-sm"
                            >
                              💾
                            </button>
                            <button
                              onClick={() => setEditId(null)}
                              className="text-gray-400 hover:text-gray-600 text-sm"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <StockBadge stock={p.stock ?? 0} />
                            <button
                              onClick={() => {
                                setEditId(p._id);
                                setEditStock(p.stock ?? 0);
                              }}
                              className="text-gray-300 hover:text-green-500 transition-colors text-xs"
                            >
                              ✏
                            </button>
                          </div>
                        )}
                      </td>

                      {/* availability toggle — unchanged */}
                      <td className="px-4 py-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!p.isAvailable}
                            onChange={() =>
                              toggleAvailable(p._id, p.isAvailable)
                            }
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 rounded-full bg-gray-200 peer-checked:bg-green-500 transition-colors" />
                          <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                        </label>
                      </td>

                      {/* actions — added Edit button alongside Delete */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setEditProduct(p)}
                            title="Edit product"
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-all"
                            style={{
                              background: "rgba(45,139,78,0.08)",
                              color: "#2D8B4E",
                              border: "1px solid rgba(45,139,78,0.2)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background =
                                "rgba(45,139,78,0.15)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                "rgba(45,139,78,0.08)";
                            }}
                          >
                            ✏ Edit
                          </button>
                          <button
                            onClick={() => setDeleteItem(p)}
                            title="Delete product"
                            className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
                          >
                            🗑
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Product Modal */}
      {editProduct && (
        <EditProductModal
          product={editProduct}
          sections={sections}
          onClose={() => setEditProduct(null)}
          onSaved={() => {
            setEditProduct(null);
            fetchInventory();
          }}
        />
      )}

      {/* Delete Confirm — unchanged */}
      {deleteItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteItem(null)}
          />
          <div className="relative bg-white rounded-3xl shadow-2xl max-w-sm w-full p-6">
            <h2 className="font-extrabold text-lg mb-4">Delete Product?</h2>
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-red-50 mb-4">
              <img
                src={deleteItem.image}
                alt={deleteItem.name}
                className="w-14 h-14 rounded-xl object-cover"
              />
              <div>
                <p className="font-bold">{deleteItem.name}</p>
                <p className="text-xs text-gray-400">
                  ₹{deleteItem.price} · {deleteItem.volume}
                </p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 mb-5">
              This product will be permanently removed. This cannot be undone.
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteItem(null)}
                className="px-5 py-2 rounded-full text-sm font-bold border border-gray-200 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={deleteProduct}
                className="px-5 py-2 rounded-full text-sm font-bold bg-red-500 text-white hover:bg-red-600 transition-colors"
              >
                🗑 Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
